import { App } from '@capacitor/app'
import { Capacitor } from '@capacitor/core'
import { useApi, useAuthContext, useEvent, useLoadable } from '@healthblocks-io/core'
import axios from 'axios'
import once from 'lodash.once'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'

import { mixpanel } from '@/mixpanel'
import { useAppInfo } from '@/modules/shared/hooks/useAppInfo'
import { useTenantHref } from '@/modules/shared/hooks/useTenantHref'
import { openUrl } from '@/utils/external-resources'
import { beginLoginAttempt, consumeLoginAttempt } from '../utils/pkce'
import { useNotificationChannel } from './useNotificationChannel'

import type { URLOpenListenerEvent } from '@capacitor/app'
import type { LoginAttemptStart } from '../utils/pkce'

type EHealthTokenPayload = {
  authenticatorId: number
  access_token: string
  refresh_token: string
  token_type: 'Bearer'
  expires_in: number
  // TODO: add proper type for account
  account?: any
  accountId?: number
}

const trackLogin = once(() => {
  mixpanel.track('account_login')
})

// Module-level set to track codes being processed or already processed across hook instances.
// This prevents multiple components from triggering the same token exchange if the code remains in the URL.
const processedCodes = new Set<string>()

function useOAuth2() {
  const api = useApi()
  const appInfo = useAppInfo()
  const [searchParameters, setSearchParameters] = useSearchParams()
  const [result, setResult] = useLoadable()
  const [isProcessingCallback, setIsProcessingCallback] = useState(false)
  const auth = useAuthContext()
  const { deleteLocalNotificationChannel } = useNotificationChannel()

  const isAuthenticated = !!auth.data.current

  const tenantHref = useTenantHref()
  const redirectUrl = useMemo(() => {
    return Capacitor.isNativePlatform()
      ? appInfo?.id
        ? `${appInfo.id}://${tenantHref('/login')}`
        : ''
      : `${window.location.origin}${tenantHref('/login')}`
  }, [appInfo?.id, tenantHref])

  // Properties
  // ------------------------------------------------------------------------- /
  const options = useMemo(() => {
    return {
      appId: appInfo?.id,
      redirectUrl: redirectUrl,
      authorizationBaseUrl: api + '/authenticator/ehealth/authorize',
      accessTokenEndpoint: api + '/authenticator/ehealth/token',
      scope: 'openid email profile',
      resourceUrl: api + '/user/me',
      responseType: 'code',
      logoutUrl: undefined,
      platform: Capacitor.getPlatform(),
    }
  }, [redirectUrl, api, appInfo])

  const optionsQueryString = useMemo(() => {
    const queryString = new URLSearchParams()

    if (options?.appId) {
      queryString.set('app_id', options.appId)
    }
    if (options?.platform) {
      queryString.set('platform', options.platform)
    }
    if (options?.redirectUrl) {
      queryString.set('redirect_uri', options.redirectUrl)
    }
    if (options?.scope) {
      queryString.set('scope', options.scope)
    }
    if (options?.responseType) {
      queryString.set('response_type', options.responseType)
    }

    return queryString.toString()
  }, [options])

  // Methods
  // ------------------------------------------------------------------------- /

  const openEHealthUrl = async (url: string) => {
    // target '_self' keeps the web path on window.location.href, which does
    // not depend on the user-activation state window.open is gated on
    await openUrl(url, { windowName: '_self', target: '_self' })
  }

  const fetchToken = useCallback(
    async (code: string, codeVerifier: string) => {
      if (!options.accessTokenEndpoint) throw new Error('No access token endpoint')

      return axios.post<EHealthTokenPayload>(options.accessTokenEndpoint, {
        code,
        platform: options.platform,
        code_verifier: codeVerifier,
      })
    },
    [options.accessTokenEndpoint, options.platform],
  )

  const handleOAuthToken = useCallback(
    async (urlParameters: URLSearchParams) => {
      const code = urlParameters.get('code')
      if (!code) return

      // Guard: Don't process the same code twice
      if (processedCodes.has(code)) {
        return
      }

      processedCodes.add(code)
      setIsProcessingCallback(true)
      setResult({ data: null, error: null, loading: true })

      try {
        // Single use — removed from storage on read. Without it the exchange
        // is doomed server-side (the code carries our challenge), so fail
        // fast with a local error instead of an opaque 400.
        const attempt = await consumeLoginAttempt()
        if (!attempt) {
          throw new Error('Login session expired, please try again')
        }
        // The server echoes the state we sent on authorize; anything else is
        // a callback this app did not start (CSRF / injected code)
        if (urlParameters.get('state') !== attempt.state) {
          throw new Error('Login session mismatch, please try again')
        }

        const tokenResponse = await fetchToken(code, attempt.verifier)
        trackLogin()

        // Delete notification channels if the user is not the same
        if (auth.data.current !== tokenResponse.data) {
          deleteLocalNotificationChannel()
        }

        auth.setData(tokenResponse.data, true)
        setResult({ data: null, error: null, loading: false })
      } catch (error) {
        let message = 'Unexpected error'
        if (error && typeof error === 'object' && 'response' in error) {
          message = (error.response as any)?.data?.message ?? 'Unexpected error'
        } else if (error instanceof Error) {
          message = error.message
        }
        // Track a sanitised shape: serialising the raw axios error would ship
        // the request body — authorization code and code_verifier included
        mixpanel.track('account_login_error', {
          message,
          status: axios.isAxiosError(error) ? error.response?.status : undefined,
        })
        console.error('Failed to fetch token in handleOAuthToken:', message)

        setResult({ data: null, error: error as Error, loading: false })
        // If it failed, maybe allow retry by removing from processedCodes?
        // For now, keep it to avoid loops.
      } finally {
        setIsProcessingCallback(false)
      }
    },
    [auth, deleteLocalNotificationChannel, fetchToken, setResult],
  )

  const onAppUrlOpen = useCallback(
    (event: URLOpenListenerEvent) => {
      const splitUrl = event.url.split('?')
      const urlParameters = new URLSearchParams(splitUrl[1])

      handleOAuthToken(urlParameters)
    },
    [handleOAuthToken],
  )

  // Effects
  // ------------------------------------------------------------------------- /
  useEffect(() => {
    let listener: Promise<any> | undefined

    if (Capacitor.isNativePlatform()) {
      // Start App Listener
      listener = App.addListener('appUrlOpen', onAppUrlOpen)
    }

    return () => {
      if (Capacitor.isNativePlatform() && listener) {
        listener.then((handle) => handle.remove())
      }
    }
  }, [onAppUrlOpen])

  useEffect(() => {
    const code = searchParameters.get('code')
    if (!code) return

    // Take the callback parameters off the URL before anything else can
    // observe them (browser history, analytics, third-party scripts) — also
    // for a stale callback URL opened while already signed in
    const remaining = new URLSearchParams(searchParameters)
    remaining.delete('code')
    remaining.delete('state')
    setSearchParameters(remaining, { replace: true })

    if (!isAuthenticated && !isProcessingCallback) {
      handleOAuthToken(searchParameters)
    }
  }, [searchParameters.toString(), isAuthenticated, isProcessingCallback])

  // Authentication actions
  const authenticate = useEvent(async () => {
    setResult({ data: null, error: null, loading: true })

    if (!options) {
      return
    }

    if (auth.data.current) {
      await signOut()
    }

    if (!options.appId && Capacitor.isNativePlatform()) {
      return alert('Probeer het later opnieuw. APP_ID_298')
    }

    // PKCE + state: keep the verifier on-device; send the S256 challenge and
    // the state along
    let loginAttempt: LoginAttemptStart
    try {
      loginAttempt = await beginLoginAttempt()
    } catch (error) {
      // Without a challenge the login cannot complete — surface the failure
      // instead of leaving the loading state stuck with no redirect
      mixpanel.track('account_login_error', {
        message: error instanceof Error ? error.message : 'PKCE setup failed',
      })
      console.error('Failed to prepare PKCE for authenticate:', error)
      setResult({ data: null, error: error as Error, loading: false })
      return
    }

    const query = new URLSearchParams(optionsQueryString)
    query.set('code_challenge', loginAttempt.codeChallenge)
    query.set('code_challenge_method', 'S256')
    query.set('state', loginAttempt.state)

    const url = `${options.authorizationBaseUrl}?${query.toString()}`
    console.log('authenticate', url)
    openEHealthUrl(url)
  })

  const signOut = useEvent(async () => {
    if (options.logoutUrl) {
      await axios.post(options.logoutUrl)
    }

    auth.signOut()
  })

  return {
    ...result,
    redirectUrl,
    authenticate,
    isProcessingCallback,
    shouldBeLoading: result.loading || isProcessingCallback,
    signOut,
  }
}

export { useOAuth2 }
